import type { PackageLimit, PackageType } from "../types";

export const TECH_BUDGET_LIMIT_EUR = 50;

export const PACKAGE_LIMITS: Record<PackageType, PackageLimit> = {
  guided_digital_access_3m: {
    durationMonths: 3,
    priceEur: 3000,
    includedDepartments: 2,
    additionalDepartmentPriceEur: 300,
    maxDepartments: 6,
    maxAiActions: 75,
    maxExistingCustomers: 50,
    maxNewCustomerAssessments: 25,
    maxExports: 3,
    techBudgetLimitEur: TECH_BUDGET_LIMIT_EUR
  },
  guided_digital_access_plus_6m: {
    durationMonths: 6,
    priceEur: 6300,
    includedDepartments: 3,
    additionalDepartmentPriceEur: 1200,
    maxDepartments: 8,
    maxAiActions: 150,
    maxExistingCustomers: 150,
    maxNewCustomerAssessments: 75,
    maxExports: 6,
    techBudgetLimitEur: TECH_BUDGET_LIMIT_EUR
  },
  guided_digital_access_executive_12m: {
    durationMonths: 12,
    priceEur: 7500,
    includedDepartments: 3,
    additionalDepartmentPriceEur: 1200,
    maxDepartments: 10,
    maxAiActions: 250,
    maxExistingCustomers: 300,
    maxNewCustomerAssessments: 150,
    maxExports: 12,
    techBudgetLimitEur: TECH_BUDGET_LIMIT_EUR
  },
  monthly_subscription: {
    durationMonths: 1,
    priceEur: 2500,
    includedDepartments: 2,
    additionalDepartmentPriceEur: 150,
    maxDepartments: 8,
    maxAiActions: 75,
    maxExistingCustomers: 50,
    maxNewCustomerAssessments: 25,
    maxExports: 3,
    techBudgetLimitEur: TECH_BUDGET_LIMIT_EUR
  },
  deal_win_engine_single_12m: {
    durationMonths: 12,
    priceEur: 780,
    includedDepartments: 0,
    additionalDepartmentPriceEur: 0,
    maxDepartments: 0,
    maxAiActions: 75,
    maxExistingCustomers: 0,
    maxNewCustomerAssessments: 0,
    maxExports: 3,
    techBudgetLimitEur: TECH_BUDGET_LIMIT_EUR
  },
  deal_win_engine_team3_12m: {
    durationMonths: 12,
    priceEur: 2160,
    includedDepartments: 0,
    additionalDepartmentPriceEur: 0,
    maxDepartments: 0,
    maxAiActions: 150,
    maxExistingCustomers: 0,
    maxNewCustomerAssessments: 0,
    maxExports: 6,
    techBudgetLimitEur: TECH_BUDGET_LIMIT_EUR
  },
  deal_win_engine_team5_12m: {
    durationMonths: 12,
    priceEur: 3000,
    includedDepartments: 0,
    additionalDepartmentPriceEur: 0,
    maxDepartments: 0,
    maxAiActions: 250,
    maxExistingCustomers: 0,
    maxNewCustomerAssessments: 0,
    maxExports: 10,
    techBudgetLimitEur: TECH_BUDGET_LIMIT_EUR
  },
  deal_win_engine_team10_12m: {
    durationMonths: 12,
    priceEur: 5400,
    includedDepartments: 0,
    additionalDepartmentPriceEur: 0,
    maxDepartments: 0,
    maxAiActions: 500,
    maxExistingCustomers: 0,
    maxNewCustomerAssessments: 0,
    maxExports: 20,
    techBudgetLimitEur: TECH_BUDGET_LIMIT_EUR
  }
};

export function getPackageLimit(packageType: PackageType): PackageLimit {
  return PACKAGE_LIMITS[packageType];
}

export function getBaseIncludedDepartments(packageType: PackageType): number {
  return getPackageLimit(packageType).includedDepartments;
}

export function getAdditionalDepartmentPrice(packageType: PackageType): number {
  return getPackageLimit(packageType).additionalDepartmentPriceEur;
}

export function getMaxDepartments(packageType: PackageType): number {
  return getPackageLimit(packageType).maxDepartments;
}

export function getPackageDurationMonths(packageType: PackageType): number {
  return getPackageLimit(packageType).durationMonths;
}

export function isEnterpriseRequired(departmentCount: number): boolean {
  return departmentCount > 10;
}

export function calculateWorkspacePrice(packageType: PackageType, departmentCount: number): number {
  const limit = getPackageLimit(packageType);
  const additionalDepartments = Math.max(0, departmentCount - limit.includedDepartments);
  return limit.priceEur + additionalDepartments * limit.additionalDepartmentPriceEur;
}

export function getPackageEndDate(startDate: string, packageType: PackageType): string {
  const date = new Date(`${startDate}T00:00:00.000Z`);
  date.setUTCMonth(date.getUTCMonth() + getPackageLimit(packageType).durationMonths);
  return date.toISOString().slice(0, 10);
}

export function getRemainingDays(endDate: string, now = new Date()): number {
  const end = new Date(`${endDate}T23:59:59.999Z`);
  const diffMs = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / 86_400_000));
}

export function isWorkspaceExpired(endDate: string, now = new Date()): boolean {
  return getRemainingDays(endDate, now) === 0;
}

export function getPackageLabel(packageType: PackageType, language: "de" | "en" = "de"): string {
  const names: Record<"de" | "en", Record<PackageType, string>> = {
    de: {
      guided_digital_access_3m: "Guided Digital Access · 3 Monate",
      guided_digital_access_plus_6m: "Guided Digital Access Plus · 6 Monate",
      guided_digital_access_executive_12m: "Guided Digital Access Executive · 12 Monate",
      monthly_subscription: "Monthly Subscription",
      deal_win_engine_single_12m: "Deal Win Engine · Einzelzugang",
      deal_win_engine_team3_12m: "Deal Win Engine · Team 3",
      deal_win_engine_team5_12m: "Deal Win Engine · Team 5",
      deal_win_engine_team10_12m: "Deal Win Engine · Team 10"
    },
    en: {
      guided_digital_access_3m: "Guided Digital Access · 3 months",
      guided_digital_access_plus_6m: "Guided Digital Access Plus · 6 months",
      guided_digital_access_executive_12m: "Guided Digital Access Executive · 12 months",
      monthly_subscription: "Monthly Subscription",
      deal_win_engine_single_12m: "Deal Win Engine · Individual Access",
      deal_win_engine_team3_12m: "Deal Win Engine · Team 3",
      deal_win_engine_team5_12m: "Deal Win Engine · Team 5",
      deal_win_engine_team10_12m: "Deal Win Engine · Team 10"
    }
  };

  return names[language][packageType];
}

export const formatPackageName = getPackageLabel;
